import React, { useEffect, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { collection, getDocs, doc, updateDoc, query, orderBy, getDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { FiCheck, FiX, FiClock, FiRefreshCw, FiSend, FiUser, FiAlertCircle } from 'react-icons/fi';
import { db } from '../../config/firebase';
import { colors, typography } from '../../theme/colors';

// Pending feature-access requests submitted from the dashboard widget.
// Approving toggles `enabledModules[moduleId] = true` on the company doc and
// queues an email to the requester via the existing mailQueue mechanism.
// Declining just records the status (and an optional reason) so the user
// sees the outcome on their next dashboard load.
//
// Permission: super admin only — Firestore rules enforce both reads and
// status updates against /featureRequests/{id}.

const STATUS_FILTERS = [
  { id: 'pending',  label: 'Pending review' },
  { id: 'approved', label: 'Approved' },
  { id: 'declined', label: 'Declined' },
  { id: 'all',      label: 'All' },
];

const FeatureRequestsTab = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);
  const [declineDraftFor, setDeclineDraftFor] = useState(null);
  const [declineReason, setDeclineReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Sort newest-first inside Firestore. Status filter is in-memory so a
      // super admin can flip between Pending / Approved / Declined without
      // refetching.
      const q = query(collection(db, 'featureRequests'), orderBy('requestedAt', 'desc'));
      const snap = await getDocs(q);
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('[FeatureRequestsTab] load:', err);
      setError(err?.message || 'Could not load requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'all') return requests;
    return requests.filter(r => (r.status || 'pending') === filter);
  }, [requests, filter]);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, declined: 0 };
    requests.forEach(r => { c[r.status || 'pending'] = (c[r.status || 'pending'] || 0) + 1; });
    return c;
  }, [requests]);

  const sendEmailNotification = async (req, decision, reason) => {
    if (!req.requestedByEmail) return; // no email to notify
    try {
      const subject = decision === 'approved'
        ? `Your AmalERP feature request was approved`
        : `Your AmalERP feature request was declined`;
      const body = decision === 'approved'
        ? `<p>Hi ${req.requestedByName || ''},</p><p>Your request for <strong>${req.moduleTitle || req.moduleId}</strong> has been approved. Refresh AmalERP to start using the feature.</p>`
        : `<p>Hi ${req.requestedByName || ''},</p><p>Your request for <strong>${req.moduleTitle || req.moduleId}</strong> was not approved at this time.</p>${reason ? `<p>Reason: ${reason}</p>` : ''}`;
      await addDoc(collection(db, 'mailQueue'), {
        to: req.requestedByEmail,
        subject,
        html: body,
        companyId: req.companyId,
        companyName: req.companyName,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.warn('[FeatureRequestsTab] email queue write failed:', err?.message);
    }
  };

  const approve = async (req) => {
    if (!req.companyId || !req.moduleId) return;
    setBusyId(req.id);
    try {
      // 1. Toggle the module on for that company.
      const companyRef = doc(db, 'companies', req.companyId);
      const companySnap = await getDoc(companyRef);
      if (!companySnap.exists()) throw new Error('Company no longer exists.');
      const enabled = { ...(companySnap.data().enabledModules || {}) };
      enabled[req.moduleId] = true;
      await updateDoc(companyRef, { enabledModules: enabled });

      // 2. Update the request status.
      await updateDoc(doc(db, 'featureRequests', req.id), {
        status: 'approved',
        approvedBy: 'super-admin',
        approvedAt: new Date().toISOString(),
      });

      // 3. Best-effort email notification.
      await sendEmailNotification(req, 'approved');

      await load();
    } catch (err) {
      console.error('[FeatureRequestsTab] approve failed:', err);
      setError(err?.message || 'Could not approve request.');
    } finally {
      setBusyId(null);
    }
  };

  const decline = async (req, reason) => {
    if (!req.id) return;
    setBusyId(req.id);
    try {
      await updateDoc(doc(db, 'featureRequests', req.id), {
        status: 'declined',
        declinedBy: 'super-admin',
        declinedAt: new Date().toISOString(),
        declineReason: reason || '',
      });
      await sendEmailNotification(req, 'declined', reason);
      setDeclineDraftFor(null);
      setDeclineReason('');
      await load();
    } catch (err) {
      console.error('[FeatureRequestsTab] decline failed:', err);
      setError(err?.message || 'Could not decline request.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Wrap>
      <Header>
        <HeaderLeft>
          <Title>Feature Requests</Title>
          <Subtitle>Companies asking for permanent access to features they tried.</Subtitle>
        </HeaderLeft>
        <HeaderActions>
          <Btn onClick={load} disabled={loading}>
            <FiRefreshCw className={loading ? 'spin' : ''} /> Reload
          </Btn>
        </HeaderActions>
      </Header>

      {error && <ErrorBox><FiAlertCircle /> {error}</ErrorBox>}

      <FilterBar>
        {STATUS_FILTERS.map(f => (
          <FilterPill
            key={f.id}
            $active={filter === f.id}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
            {f.id !== 'all' && counts[f.id] > 0 && (
              <FilterCount $active={filter === f.id}>{counts[f.id]}</FilterCount>
            )}
          </FilterPill>
        ))}
      </FilterBar>

      {filtered.length === 0 && !loading && (
        <EmptyBox>
          <FiSend size={20} />
          <span>{filter === 'pending' ? 'No pending requests right now.' : 'No requests in this view.'}</span>
        </EmptyBox>
      )}

      <List>
        {filtered.map(req => {
          const isBusy = busyId === req.id;
          const isDeclining = declineDraftFor === req.id;
          const status = req.status || 'pending';
          return (
            <Item key={req.id} $status={status}>
              <ItemMain>
                <ItemTopRow>
                  <CompanyName>{req.companyName || req.companyId}</CompanyName>
                  <StatusPill $status={status}>
                    {status === 'pending' && <><FiClock size={11} /> Pending</>}
                    {status === 'approved' && <><FiCheck size={11} /> Approved</>}
                    {status === 'declined' && <><FiX size={11} /> Declined</>}
                  </StatusPill>
                </ItemTopRow>
                <ModuleLine>
                  <ModuleId>{req.moduleId}</ModuleId>
                  {req.category && <CategoryChip>{req.category}</CategoryChip>}
                  {req.moduleTitle && req.moduleTitle !== req.moduleId && (
                    <ModuleTitleText>· {req.moduleTitle}</ModuleTitleText>
                  )}
                </ModuleLine>
                <Meta>
                  <FiUser size={11} /> {req.requestedByName || req.requestedByEmail || req.requestedBy || '—'}
                  {req.requestedByEmail && req.requestedByName !== req.requestedByEmail && (
                    <> · <span style={{ opacity: 0.8 }}>{req.requestedByEmail}</span></>
                  )}
                  <span style={{ marginLeft: 'auto' }}>
                    <FiClock size={11} style={{ verticalAlign: 'middle' }} />{' '}
                    {req.requestedAt ? new Date(req.requestedAt).toLocaleString() : '—'}
                  </span>
                </Meta>
                {status === 'declined' && req.declineReason && (
                  <DeclineReasonText><strong>Reason:</strong> {req.declineReason}</DeclineReasonText>
                )}
                {isDeclining && (
                  <DeclineForm>
                    <DeclineInput
                      placeholder="Optional reason (sent to the requester)"
                      value={declineReason}
                      onChange={e => setDeclineReason(e.target.value)}
                      autoFocus
                    />
                    <Btn $danger onClick={() => decline(req, declineReason.trim())} disabled={isBusy}>
                      Confirm Decline
                    </Btn>
                    <Btn onClick={() => { setDeclineDraftFor(null); setDeclineReason(''); }}>
                      Cancel
                    </Btn>
                  </DeclineForm>
                )}
              </ItemMain>
              {status === 'pending' && !isDeclining && (
                <ItemActions>
                  <Btn $primary disabled={isBusy} onClick={() => approve(req)}>
                    <FiCheck /> {isBusy ? 'Approving…' : 'Approve & Enable'}
                  </Btn>
                  <Btn $danger disabled={isBusy} onClick={() => { setDeclineDraftFor(req.id); setDeclineReason(''); }}>
                    <FiX /> Decline
                  </Btn>
                </ItemActions>
              )}
            </Item>
          );
        })}
      </List>
    </Wrap>
  );
};

export default FeatureRequestsTab;

// ── styled ──────────────────────────────────────────────────────────────────
const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 14px;
  padding: 16px 0;
`;
const Header = styled.div`
  display: flex; justify-content: space-between; align-items: flex-end; gap: 12px;
`;
const HeaderLeft = styled.div``;
const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.text.primary};
`;
const Subtitle = styled.div`
  font-size: 13px;
  color: ${colors.text.secondary};
  margin-top: 4px;
`;
const HeaderActions = styled.div`
  display: flex; gap: 8px;
`;
const Btn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px;
  background: ${p => p.$primary ? colors.primary.main : (p.$danger ? colors.error.main : 'white')};
  color: ${p => (p.$primary || p.$danger) ? 'white' : colors.text.primary};
  border: 1px solid ${p => p.$primary ? colors.primary.main : (p.$danger ? colors.error.main : (colors.neutral?.[200] || '#e5e7eb'))};
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { opacity: 0.92; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;
const FilterBar = styled.div`
  display: flex; gap: 6px;
`;
const FilterPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${p => p.$active ? colors.primary.main : 'white'};
  color: ${p => p.$active ? 'white' : colors.text.primary};
  border: 1px solid ${p => p.$active ? colors.primary.main : (colors.neutral?.[200] || '#e5e7eb')};
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: ${colors.primary.main}; }
`;
const FilterCount = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 9px;
  background: ${p => p.$active ? 'rgba(255,255,255,0.25)' : `${colors.primary.main}18`};
  color: ${p => p.$active ? 'white' : colors.primary.main};
`;
const List = styled.div`
  display: flex; flex-direction: column; gap: 10px;
`;
const Item = styled.div`
  display: flex; gap: 14px;
  padding: 14px 16px;
  background: white;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-left: 4px solid ${p => {
    if (p.$status === 'approved') return colors.success.main;
    if (p.$status === 'declined') return colors.error.main;
    return colors.warning.main;
  }};
  border-radius: 6px;
`;
const ItemMain = styled.div`
  flex: 1;
  display: flex; flex-direction: column; gap: 6px;
  min-width: 0;
`;
const ItemTopRow = styled.div`
  display: flex; align-items: center; gap: 10px;
`;
const CompanyName = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${colors.text.primary};
`;
const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  ${p => {
    const c = p.$status === 'approved' ? colors.success.main
      : p.$status === 'declined' ? colors.error.main
      : colors.warning.main;
    return `color: ${c}; background: ${c}18;`;
  }}
`;
const ModuleLine = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
`;
const ModuleId = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${colors.primary.main};
  font-family: ${typography.fontFamily.mono};
`;
const CategoryChip = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
`;
const ModuleTitleText = styled.span`
  font-size: 13px;
  color: ${colors.text.secondary};
`;
const Meta = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: ${colors.text.secondary};
  flex-wrap: wrap;
`;
const DeclineReasonText = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  background: ${colors.background?.paper || '#fafafa'};
  border-left: 3px solid ${colors.error.main}50;
  padding: 6px 10px;
  border-radius: 0 4px 4px 0;
  margin-top: 4px;
  strong { color: ${colors.text.primary}; }
`;
const DeclineForm = styled.div`
  display: flex; gap: 6px; align-items: center; flex-wrap: wrap;
  margin-top: 4px;
`;
const DeclineInput = styled.input`
  flex: 1;
  min-width: 240px;
  padding: 7px 10px;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 4px;
  font-size: 13px;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;
const ItemActions = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  align-items: stretch;
  flex-shrink: 0;
`;
const ErrorBox = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 14px;
  background: ${colors.error.main}10;
  border: 1px solid ${colors.error.main};
  border-radius: 4px;
  color: ${colors.error.main};
  font-size: 13px;
  font-weight: 500;
`;
const EmptyBox = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 40px 20px;
  background: white;
  border: 1px dashed ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 6px;
  color: ${colors.text.secondary};
  font-size: 13px;
  font-weight: 500;
  gap: 8px;
`;
